import { useCallback, useEffect, useRef, useState } from 'react'
import type { ProductListItem, ProductListQuery } from '../types'
import { fetchProducts } from '../services/productApi'
import { DEFAULT_PRODUCT_LIST_QUERY } from '../constants'
import {
  clearProductListSessionFilter,
  readProductListSessionFilter,
  writeProductListSessionFilter,
} from '../utils/productSessionFilter'

export function useProductList() {
  const [query, setQuery] = useState<ProductListQuery>(() => {
    const saved = readProductListSessionFilter()
    return { ...DEFAULT_PRODUCT_LIST_QUERY, ...saved }
  })

  const filterSearchValue = query.search || ''
  const [searchDraft, setSearchDraft] = useState({
    source: filterSearchValue,
    value: filterSearchValue,
  })
  const searchInput =
    searchDraft.source === filterSearchValue ? searchDraft.value : filterSearchValue

  const [statusFilter, setStatusFilter] = useState<string>(() =>
    query.is_active !== undefined && query.is_active !== '' ? String(query.is_active) : '',
  )
  const [rows, setRows] = useState<ProductListItem[]>([])
  const [pageMeta, setPageMeta] = useState({ current: 1, total: 0, last: 1 })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const isInitialMount = useRef(true)

  const load = useCallback(async (q: ProductListQuery) => {
    setLoading(true)
    setError(null)
    try {
      const res = await fetchProducts(q)
      setRows(res.data || [])
      setPageMeta({
        current: res.current ?? 1,
        total: res.total ?? 0,
        last: res.last ?? 1,
      })
    } catch (e) {
      setRows([])
      setPageMeta({ current: 1, total: 0, last: 1 })
      setError((e as { message?: string }).message || 'Failed to load products')
    } finally {
      setLoading(false)
    }
  }, [])

  const applyFilters = useCallback(
    (overrides?: { search?: string; is_active?: string }) => {
      const targetSearch = overrides?.search !== undefined ? overrides.search : searchInput
      const targetStatus =
        overrides?.is_active !== undefined
          ? overrides.is_active
          : statusFilter === ''
            ? undefined
            : statusFilter

      const trimmedSearch = typeof targetSearch === 'string' ? targetSearch.trim() : undefined
      const next: ProductListQuery = {
        ...query,
        page: 1,
        search: trimmedSearch || undefined,
        is_active: targetStatus === '' || targetStatus === undefined ? undefined : targetStatus,
      }
      if (!next.search) delete next.search
      if (next.is_active === undefined) delete next.is_active
      setQuery(next)
      writeProductListSessionFilter(next)
      void load(next)
    },
    [query, searchInput, statusFilter, load],
  )

  const setSearchInput = useCallback(
    (val: string) => {
      setSearchDraft({
        source: filterSearchValue,
        value: val,
      })
    },
    [filterSearchValue],
  )

  const handleStatusChange = useCallback(
    (newStatus: string) => {
      setStatusFilter(newStatus)
      applyFilters({ is_active: newStatus })
    },
    [applyFilters],
  )

  // Debounce search input changes (500ms debounce matching Application module pattern)
  useEffect(() => {
    if (isInitialMount.current) {
      return
    }

    const timer = setTimeout(() => {
      const currentApplied = (query.search || '').trim()
      const draftTrimmed = searchInput.trim()
      if (draftTrimmed !== currentApplied) {
        applyFilters({ search: searchInput })
      }
    }, 500)

    return () => clearTimeout(timer)
  }, [searchInput, query.search, applyFilters])

  const resetFilters = useCallback(() => {
    clearProductListSessionFilter()
    const next = { ...DEFAULT_PRODUCT_LIST_QUERY }
    setQuery(next)
    setSearchDraft({
      source: '',
      value: '',
    })
    setStatusFilter('')
    void load(next)
  }, [load])

  const changePage = useCallback(
    (page: number) => {
      const next = { ...query, page }
      setQuery(next)
      writeProductListSessionFilter(next)
      void load(next)
    },
    [query, load],
  )

  const changePerPage = useCallback(
    (perPage: number) => {
      const next = { ...query, perPage, page: 1 }
      setQuery(next)
      writeProductListSessionFilter(next)
      void load(next)
    },
    [query, load],
  )

  const sort = useCallback(
    (orderBy: string) => {
      const next: ProductListQuery = {
        ...query,
        orderBy,
        /** Angular product list flips the current direction unconditionally and keeps the current page. */
        sortBy: query.sortBy === 'asc' ? 'desc' : 'asc',
      }
      setQuery(next)
      writeProductListSessionFilter(next)
      void load(next)
    },
    [query, load],
  )

  const refetch = useCallback(() => {
    void load(query)
  }, [query, load])

  useEffect(() => {
    void load(query)
    isInitialMount.current = false
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial load only
  }, [])

  return {
    rows,
    loading,
    error,
    query,
    searchInput,
    setSearchInput,
    statusFilter,
    setStatusFilter,
    handleStatusChange,
    pageMeta,
    applyFilters,
    resetFilters,
    changePage,
    changePerPage,
    sort,
    refetch,
  }
}
